import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

import OrderItem from './OrderItem';
import { NotificationMessage } from './NotificationMessage';

registerLocale('pt-BR', ptBR);

function OrderList() {
    const [orders, setOrders] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [estados, setEstados] = useState([]);

    const [notification, setNotification] = useState({
        type: 'info',
        message: '',
        show: false,
    });

    const [filters, setFilters] = useState({
        OrderNumber: '',
        customerName: '',
        uf: '',
        channel: '',
        supplier: '',
        origin: '',
        status: '',
        dateFrom: '',
        dateTo: '',
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [openDateRange, setOpenDateRange] = useState(false);
    const [resultsPerPage, setResultsPerPage] = useState(20);
    
    const navigate = useNavigate();
    
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        setFilters(prev => ({
            ...prev,
            dateFrom: start ? format(start, 'dd/MM/yyyy') : '',
            dateTo: end ? format(end, 'dd/MM/yyyy') : '',
        }));

        if (start && end) {
            const updatedFilters = {
                ...filters,
                dateFrom: start ? format(start, 'dd/MM/yyyy') : '',
                dateTo: end ? format(end, 'dd/MM/yyyy') : '',
            };

            updateUrlParams(updatedFilters);
        }
    };

    const updateUrlParams = (newFilters) => {
        const filteredParams = Object.fromEntries(
            Object.entries(newFilters).filter(([_, value]) => value !== '' && value !== null)
        );

        const params = new URLSearchParams(filteredParams).toString();
        navigate(`?${params}`);
    };

    const fetchOrders = async () => {
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await fetch(`${baseUrl}/orders`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os pedidos.');
            }

            const data = await response.json();
            setOrders(data.data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchEstados = async () => {
        try {
            const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
            if (!response.ok) {
                throw new Error('Erro ao buscar os estados.');
            }
            const data = await response.json();
            setEstados(data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        for (const param of searchParams) {
            setFilters(prev => ({
                ...prev,
                [param[0]]: param[1]
            }));
        }

        fetchOrders();
        fetchEstados();
    }, []);

    const handleOrderSent = (message) => {
        setNotification({ type: 'success', message: message, show: true });

        fetchOrders();
    };

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, show: false }));
    };

    const handleSelectAll = (isChecked) => {
        if (isChecked) {
            setSelectedOrders(orders.map(order => order.id));
        } else {
            setSelectedOrders([]);
        }
    };

    const isAllSelected = selectedOrders.length === orders.length && orders.length > 0;

    const handleSelectOrder = (orderId) => {
        setSelectedOrders(prevSelected => 
            prevSelected.includes(orderId)
                ? prevSelected.filter(id => id !== orderId)
                : [...prevSelected, orderId]
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = {
            ...filters,
            [name]: value,
        };
        setFilters(updatedFilters);
        updateUrlParams(updatedFilters);
    };

    const handleDeleteSelected = async () => {
        const idsString = selectedOrders.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/orders/${idsString}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });

                throw new Error('Erro ao excluir os itens.');
            }

            setNotification({
                type: 'success',
                message: 'Pedidos excluídos com sucesso',
                show: true
            });

            setSelectedOrders([]);
            fetchOrders();
        } catch (error) {
            console.error('Erro ao excluir itens:', error);
        }
    };

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const matchesOrderNumber = order.order_number.toString().includes(filters.OrderNumber);
            const matchesCustomerName = order.customer.name.toLowerCase().includes(filters.customerName.toLowerCase());
            const matchesUf = filters.uf ? order.shippings[0].state === filters.uf : true;
            const matchesChannel = filters.channel
                ? (order.sales_channel?.name || '').toLowerCase().includes(filters.channel.toLowerCase())
                : true;
            const matchesSupplier = order.supplier.name.toLowerCase().includes(filters.supplier.toLowerCase());
            const matchesOrigin = filters.origin
                ? (order.erp_status || '').toLowerCase().includes(filters.origin.toLowerCase())
                : true;
            const matchesStatus = order.translated_status.toLowerCase().includes(filters.status.toLowerCase());
            const matchesDateFrom = (() => {
                if (!filters.dateFrom && !filters.dateTo) return true;
                
                const orderDate = parse(order.created_at, 'dd/MM/yyyy HH:mm:ss', new Date());
                
                const dateFromObj = filters.dateFrom ? parse(filters.dateFrom, 'dd/MM/yyyy', new Date()) : null;
                const dateToObj = filters.dateTo ? parse(filters.dateTo, 'dd/MM/yyyy', new Date()) : null;
                
                const matchesFrom = !dateFromObj || orderDate >= dateFromObj;
                const matchesTo = !dateToObj || orderDate <= dateToObj;
                
                return matchesFrom && matchesTo;
            })();
            
            return matchesOrderNumber && matchesCustomerName && matchesUf && matchesChannel && matchesSupplier && matchesOrigin && matchesStatus && matchesDateFrom;
        });
    }, [orders, filters]);

    const columns = useMemo(() => [
        {
            id: 'select',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                />
            ),
            Cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedOrders.includes(row.original.id)}
                    onChange={() => handleSelectOrder(row.original.id)}
                />
            ),
            headerClassName: 'p-4 text-left text-sm font-medium uppercase'
        },
        { Header: 'Nº Pedido', accessor: 'order_number', headerClassName: 'p-2 pl-0 2xl:pl-4 text-left text-sm font-medium uppercase select-none' },
        { Header: 'Pedido', accessor: 'customer.name', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
        { Header: 'Canal', accessor: 'supplier.sales_channel?.name', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
        { Header: 'Fornecedor', accessor: 'supplier.name', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
        { Header: 'Origem', accessor: 'erp_status', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
        { Header: 'Status', accessor: 'translated_status', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
        { Header: 'Data Adicionado', accessor: 'created_at', headerClassName: 'p-2 text-left 2xl:p-4 text-sm font-medium uppercase select-none' },
    ], [isAllSelected, selectedOrders]);

    const pageParams = new URLSearchParams(window.location.search);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        state: { pageIndex },
        gotoPage,
        nextPage,
        previousPage,
    } = useTable(
        {
            columns,
            data: filteredOrders,
            initialState: { pageIndex: pageParams.has('page') ? (pageParams.get('page') -1) : 0, pageSize: resultsPerPage },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (pageIndex !== 0 || searchParams.has('page')) {
            searchParams.set('page', pageIndex + 1);
            navigate(`?${searchParams.toString()}`, { replace: true });
        }
    }, [pageIndex, navigate]);

    if (loading) return (
        <div className="mx-auto py-10">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
        </div>
    );

    if (error) {
        return <div className="text-2xl text-center text-red-600 font-bold my-10">{error}</div>;
    }

    return (
        <>
            <table {...getTableProps()} className="w-full table-auto border-collapse bg-gray-2">
                <thead>
                    {headerGroups.map((headerGroup, index) => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

                        return (
                            <tr key={key || index} {...headerGroupProps}>
                                {headerGroup.headers.map((column, groupIndex) => {
                                    const { key: colKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps({ title: undefined }));

                                    return (
                                        <th
                                            key={colKey || groupIndex}
                                            {...columnProps}
                                            className={column.headerClassName}
                                        >
                                            {column.render('Header')}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <svg className="fill-gray-500 inline-block mb-1 ml-1" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 0L0 5H10L5 0Z" fill=""></path>
                                                    </svg>
                                                ) : (
                                                    <svg className="fill-gray-500 inline-block mb-1 ml-1" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill=""></path>
                                                    </svg>
                                                )
                                            ) : ''}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}

                    <tr className="bg-gray-50">
                        <td></td>
                        <td className="p-2 pl-0 text-left 2xl:p-4 xl:pl-0 text-sm">
                            <input 
                                type="text" 
                                name="OrderNumber"
                                placeholder="Número" 
                                className="w-full border border-gray-300 text-xs 2xl:text-sm p-1 max-w-16" 
                                value={filters.OrderNumber}
                                onChange={handleFilterChange}
                            />
                        </td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <input 
                                    type="text" 
                                    name="customerName"
                                    placeholder="Nome do Cliente" 
                                    className="border border-gray-300 text-xs 2xl:text-sm p-1" 
                                    value={filters.customerName}
                                    onChange={handleFilterChange}
                                />
                                <select 
                                    name="uf"
                                    className="border border-gray-300 text-xs 2xl:text-sm p-1"
                                    onChange={handleFilterChange}
                                    value={filters.uf ? filters.uf : 'UF'}
                                >
                                    <option disabled>UF</option>
                                    <option value="">Todos</option>
                                    {estados.map((estado) => (
                                        <option key={estado.id} value={estado.sigla}>
                                            {estado.sigla}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <input 
                                type="text" 
                                name="channel"
                                placeholder="Canal de Venda" 
                                className="border border-gray-300 p-1 text-xs 2xl:text-sm xl:max-w-20 2xl:max-w-32" 
                                value={filters.channel}
                                onChange={handleFilterChange}
                            />
                        </td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <input 
                                type="text" 
                                name="supplier"
                                placeholder="Fornecedor" 
                                className="border border-gray-300 p-1 text-xs 2xl:text-sm xl:max-w-32 2xl:max-w-40" 
                                value={filters.supplier}
                                onChange={handleFilterChange}
                            />
                        </td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <input 
                                type="text" 
                                name="origin"
                                placeholder="Origem" 
                                className="border border-gray-300 p-1 text-xs 2xl:text-sm xl:max-w-32 2xl:max-w-40" 
                                value={filters.origin}
                                onChange={handleFilterChange}
                            />
                        </td>
                        <td className="p-2 text-left 2xl:p-4 text-sm">
                            <select 
                                name="status"
                                className="border border-gray-300 p-1 text-xs 2xl:text-sm w-full"
                                value={filters.status ? filters.status : 'Status'}
                                onChange={handleFilterChange}
                            >
                                <option disabled>Status</option>
                                <option value="">Todos</option>
                                <option value="Importado da Bling">Importado da Bling</option>
                                <option value="Aguardando fornecedor">Aguardando fornecedor</option>
                                <option value="Aceito pelo fornecedor">Aceito pelo fornecedor</option>
                                <option value="Rejeitado pelo fornecedor">Rejeitado pelo fornecedor</option>
                                <option value="Faturamento autorizado">Faturamento autorizado</option>
                                <option value="Faturamento rejeitado">Faturamento rejeitado</option>
                                <option value="NF Enviada">NF Enviada</option>
                                <option value="Faturado Fornecedor">Faturado Fornecedor</option>
                                <option value="Arquivos enviados">Arquivos enviados</option>
                            </select>
                        </td>
                        <td className="p-2 text-left 2xl:p-4 pr-0 xl:pr-0 text-sm">
                            <div className="flex items-center">
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleDateChange}
                                    selectsRange
                                    startDate={startDate}
                                    endDate={endDate}
                                    dateFormat="dd/MM/yyyy"
                                    onCalendarOpen={() => setOpenDateRange(true)}
                                    onCalendarClose={() => setOpenDateRange(false)}
                                    placeholderText="Intervalo de datas"
                                    className="border border-gray-300 p-1 text-xs 2xl:text-sm w-full"
                                    isClearable
                                    locale="pt-BR"
                                    open={openDateRange}
                                />
                            </div>
                        </td>
                        <td className="-ml-2 py-1 text-left xl:py-4 text-sm"></td>
                        <td className="py-1 text-left xl:py-4 text-sm"></td>
                    </tr>
                </thead>
                <tbody  {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <OrderItem
                                key={row.id}
                                row={row}
                                onSelect={handleSelectOrder}
                                isSelected={selectedOrders.includes(row.original.id)}
                                orderSent={handleOrderSent}
                            />
                        );
                    })}
                </tbody>
            </table>

            <div className="flex items-center mt-6 mb-4">
                <nav className="pagination isolate inline-flex space-x-1" aria-label="Pagination">
                    <button 
                        onClick={() => previousPage()} 
                        disabled={!canPreviousPage} 
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"></path>
                        </svg>
                    </button>

                    {pageIndex !== 0 && (
                        <button 
                            onClick={() => gotoPage(0)} 
                            disabled={!canPreviousPage} 
                            className={`relative inline-flex items-center justify-center font-medium rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                        >
                            1
                        </button>
                    )}

                    {pageIndex > 1 && pageOptions.length > 4 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageOptions[pageIndex - 1] ? (
                        <button 
                            onClick={() => gotoPage(pageIndex - 1)} 
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex}
                        </button>
                    ) : null}


                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-white bg-secondary ring-1 ring-inset ring-secondary hover:bg-slate-700">
                        {pageIndex + 1}
                    </span>

                    {pageOptions[pageIndex + 1] && (
                        <button 
                            onClick={() => gotoPage(pageIndex + 1)} 
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex + 2}
                        </button>
                    )}

                    {pageIndex < pageOptions.length - 3 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-gray-400 ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageIndex < pageOptions.length - 2 && (
                        <button 
                            onClick={() => gotoPage(pageOptions.length - 1)} 
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageOptions.length}
                        </button>
                    )}

                    <button 
                        onClick={() => nextPage()} 
                        disabled={!canNextPage} 
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canNextPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Próximo</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"></path>
                        </svg>
                    </button>
                </nav>

                <button onClick={handleDeleteSelected} className="bg-red-500 text-white px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedOrders.length <= 0 ? true : false}>
                    <FontAwesomeIcon icon={faTrash} className=" mr-2" />
                    Excluir Selecionados
                </button>
            </div>

            {notification && notification.show && (
                <NotificationMessage
                    type={notification.type}
                    message={notification.message}
                    show={notification.show}
                    onClose={handleCloseNotification}
                />
            )}
        </>
    );
}

export default OrderList;
